"use client";

import { createApi } from "@reduxjs/toolkit/query/react";

import baseQuery from "@/libs/shared/api/baseQuery";
import { GetPayloadModel, GetResponseModel } from "@/libs/shared/api/api.types";
import {
  NutritionPlan,
  Food,
  NutritionLookupResponse,
} from "@/libs/nutrition-plans/redux/nutriton-plans.types";

const tagTypes = ["NutritionPlans" as const, "Foods" as const];

export const nutritionPlansApi = createApi({
  reducerPath: "nutritionPlansApi",
  baseQuery,
  tagTypes,
  endpoints: (builder) => ({
    generateToken: builder.mutation<
      { token: string },
      {
        apiKey: string;
        ptId: string;
        source: string;
      }
    >({
      query: ({ apiKey, ptId, source }) => ({
        url: `/auth/generate-token`,
        body: {
          apiKey: apiKey,
          ptId: ptId,
          source,
        },
        method: "POST",
      }),
    }),
    getNutritionPlans: builder.query<
      GetResponseModel<NutritionPlan>,
      Partial<GetPayloadModel>
    >({
      query: ({ page, perPage, name, orderBy, orderByDirection }) => ({
        url: `/nutrition-plans?page=${page}&per_page=${perPage}&name=${name}&order_by=${orderBy}&order_by_direction=${orderByDirection}&is_model=1`,
      }),
      providesTags: ["NutritionPlans"],
    }),
    getAssignedNutritionPlans: builder.query<
      GetResponseModel<NutritionPlan>,
      Partial<GetPayloadModel>
    >({
      query: ({
        page,
        perPage,
        name,
        orderBy,
        orderByDirection,
        customerId,
        groupId,
      }) => {
        const query = new URLSearchParams({
          page: page || "",
          per_page: perPage || "",
          name: name || "",
          order_by: orderBy || "",
          order_by_direction: orderByDirection || "",
        });

        if (customerId) {
          query.set("customer_id", customerId);
        }

        if (groupId) {
          query.set("group_id", groupId);
        }

        return {
          url: `/nutrition-plans/assigned?${query.toString()}`,
        };
      },
      providesTags: ["NutritionPlans"],
    }),
    getFoods: builder.query<
      GetResponseModel<Food>,
      Partial<GetPayloadModel & { template: string }>
    >({
      query: ({
        page,
        perPage,
        name,
        orderBy,
        orderByDirection,
        template,
      }) => ({
        url: `/foods?page=${page}&per_page=${perPage}&name=${name}&order_by=${orderBy}&order_by_direction=${orderByDirection}&template=${template}`,
      }),
      providesTags: ["Foods"],
    }),
    deleteFoods: builder.mutation<void, string[]>({
      query: (ids: string[]) => {
        const idsString = ids?.toString() || "";
        return {
          url: `/foods?foodId=${idsString}`,
          method: "DELETE",
        };
      },
      // Invalidate foods list after deletion
      onQueryStarted: (_arg, api) => {
        return api.queryFulfilled.then(() => {
          api.dispatch(nutritionPlansApi.util.invalidateTags(["Foods"]));
        });
      },
    }),
    deleteNutritionPlan: builder.mutation<void, string>({
      query: (id: string) => ({
        url: `/nutrition-plans/${id}`,
        method: "DELETE",
      }),
      onQueryStarted: (_arg, api) => {
        return api.queryFulfilled.then(() => {
          api.dispatch(
            nutritionPlansApi.util.invalidateTags(["NutritionPlans"]),
          );
        });
      },
    }),
    duplicateNutritionPlan: builder.mutation<
      void,
      { id: string; name: string }
    >({
      query: ({ id, name }) => ({
        url: `/nutrition-plans/${id}/duplicate`,
        method: "POST",
        body: { name },
      }),
      onQueryStarted: (_arg, api) => {
        return api.queryFulfilled.then(() => {
          api.dispatch(
            nutritionPlansApi.util.invalidateTags(["NutritionPlans"]),
          );
        });
      },
    }),
    assignNutritionPlan: builder.mutation<
      void,
      { id: string; customerIds?: number[] | null; groupIds?: number[] | null }
    >({
      query: ({ id, customerIds = null, groupIds = null }) => ({
        url: `/nutrition-plans/${id}/assign`,
        method: "POST",
        body: { customerIds, groupIds },
      }),
      onQueryStarted: (_arg, api) => {
        return api.queryFulfilled.then(() => {
          api.dispatch(
            nutritionPlansApi.util.invalidateTags(["NutritionPlans"]),
          );
        });
      },
    }),
    toggleNutritionPlan: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `/nutrition-plans/${id}/toggle-active`,
        method: "PATCH",
      }),
      onQueryStarted: (_arg, api) => {
        return api.queryFulfilled.then(() => {
          api.dispatch(
            nutritionPlansApi.util.invalidateTags(["NutritionPlans"]),
          );
        });
      },
    }),
    lookupNutritionAssignments: builder.query<
      NutritionLookupResponse,
      {
        customerIds?: number[] | string[] | string;
        groupIds?: number[] | string[] | string;
      }
    >({
      query: ({ customerIds, groupIds }) => {
        const params = new URLSearchParams();

        const appendParam = (
          key: "customerIds" | "groupIds",
          value?: number[] | string[] | string,
        ) => {
          if (value === undefined || value === null) return;

          if (Array.isArray(value)) {
            value
              .map((item) => item?.toString?.() ?? "")
              .filter(Boolean)
              .forEach((item) => params.append(`${key}[]`, item));
          } else {
            const serialized = value.toString();
            if (serialized.includes(",")) {
              params.set(key, serialized);
            } else {
              params.set(key, serialized);
            }
          }
        };

        appendParam("customerIds", customerIds);
        appendParam("groupIds", groupIds);

        return {
          url: `/pt/lookup${params.toString() ? `?${params.toString()}` : ""}`,
        };
      },
    }),
  }),
});

export const {
  useGetNutritionPlansQuery,
  useGetAssignedNutritionPlansQuery,
  useGenerateTokenMutation,
  useGetFoodsQuery,
  useDeleteFoodsMutation,
  useDeleteNutritionPlanMutation,
  useToggleNutritionPlanMutation,
  useDuplicateNutritionPlanMutation,
  useAssignNutritionPlanMutation,
  useLookupNutritionAssignmentsQuery,
} = nutritionPlansApi;
